import React from 'react';
import { CharacterPortrait, ChoiceItem } from '../../types';
import {
  PEDA_CATEGORY_KEYS,
  PEDA_CATEGORY_LABELS,
  type PedaCategoryKey,
} from '../../systems/peda-categories';
import { InlineEditableText } from './InlineEditableText';
import inline from './inline-editor.module.css';
import studio from './scenario-studio.module.css';
import type { InlineScenarioChangeOptions } from './use-inline-block-history';

interface InlineChoiceEditorProps {
  item: ChoiceItem;
  characters: CharacterPortrait[];
  onChange: (item: ChoiceItem, options?: InlineScenarioChangeOptions) => void;
  renderBranch: (optionIndex: number, optionText: string) => React.ReactNode;
}

export const InlineChoiceEditor: React.FC<InlineChoiceEditorProps> = ({
  item,
  characters,
  onChange,
  renderBranch,
}) => {
  const updateOption = (idx: number, patch: Partial<ChoiceItem['options'][0]>) => {
    const options = item.options.map((o, i) => (i === idx ? { ...o, ...patch } : o));
    onChange({ ...item, options });
  };

  const updateOptionText = (idx: number, text: string) => {
    updateOption(idx, { text });
  };

  const removeOption = (idx: number) => {
    onChange(
      { ...item, options: item.options.filter((_, i) => i !== idx) },
      { recordBlockHistory: true }
    );
  };

  const addOptionAfter = (idx: number) => {
    const options = [...item.options];
    options.splice(idx + 1, 0, { text: 'Nouvelle option' });
    onChange({ ...item, options }, { recordBlockHistory: true });
  };

  const moveOption = (idx: number, dir: -1 | 1) => {
    const target = idx + dir;
    if (target < 0 || target >= item.options.length) return;
    const options = [...item.options];
    [options[idx], options[target]] = [options[target]!, options[idx]!];
    onChange({ ...item, options }, { recordBlockHistory: true });
  };

  const setStopButton = (idx: number, checked: boolean) => {
    const options = item.options.map((o, i) => {
      if (i === idx) {
        return { ...o, stopButton: checked || undefined };
      }
      if (checked) {
        return { ...o, stopButton: undefined };
      }
      return o;
    });
    onChange({ ...item, options });
  };

  return (
    <div className={inline.choiceEditor}>
      {item.options.length === 0 && (
        <button
          type="button"
          className={studio.btnSecondary}
          onClick={() =>
            onChange({ ...item, options: [{ text: 'Nouvelle option' }] }, { recordBlockHistory: true })
          }
        >
          + Réponse
        </button>
      )}
      {item.options.map((opt, idx) => (
        <div key={idx} className={inline.choiceOptionBlock}>
          <div className={inline.choiceOptionRow}>
            <span className={inline.choiceOptionIndex} aria-hidden>
              ▶
            </span>
            <InlineEditableText
              value={opt.text}
              className={inline.choiceOptionText}
              onChange={(text) => updateOptionText(idx, text)}
              placeholder="Texte de la réponse…"
            />
            <div className={inline.choiceOptionRight}>
              <div className={inline.choiceOptionToggles}>
                <label className={inline.choiceShieldToggle}>
                  <input
                    type="checkbox"
                    checked={(opt.pedaShields ?? 0) > 0}
                    onChange={(e) => {
                      if (e.target.checked) {
                        updateOption(idx, {
                          pedaShields: opt.pedaShields && opt.pedaShields > 0 ? opt.pedaShields : 1,
                        });
                      } else {
                        updateOption(idx, { pedaShields: undefined, pedaCategory: undefined });
                      }
                    }}
                  />
                  <span className={inline.metaFieldLabel}>Boucliers PEDA</span>
                </label>
                {(opt.pedaShields ?? 0) > 0 && (
                  <input
                    type="number"
                    min={1}
                    className={inline.choiceOptionQtyInput}
                    value={opt.pedaShields ?? 1}
                    onChange={(e) => {
                      const n = parseInt(e.target.value, 10);
                      updateOption(idx, { pedaShields: n > 0 ? n : 1 });
                    }}
                    title="Quantité de boucliers"
                    aria-label="Quantité de boucliers"
                  />
                )}
                <label className={inline.choiceShieldToggle}>
                  <input
                    type="checkbox"
                    checked={Boolean(opt.stopButton)}
                    onChange={(e) => setStopButton(idx, e.target.checked)}
                  />
                  <span className={inline.metaFieldLabel}>Bouton STOP</span>
                </label>
                <label className={inline.choiceShieldToggle}>
                  <input
                    type="checkbox"
                    checked={(opt.confidenceGains?.length ?? 0) > 0}
                    onChange={(e) => {
                      if (e.target.checked) {
                        const firstChar = characters[0];
                        updateOption(idx, {
                          confidenceGains: opt.confidenceGains?.length
                            ? opt.confidenceGains
                            : firstChar
                              ? [{ characterId: firstChar.id, amount: 10 }]
                              : [],
                        });
                      } else {
                        updateOption(idx, { confidenceGains: undefined });
                      }
                    }}
                    disabled={characters.length === 0}
                  />
                  <span className={inline.metaFieldLabel}>Confiance</span>
                </label>
              </div>
              <div className={inline.choiceOptionActions}>
              <button
                type="button"
                className={inline.choiceOptionMove}
                onClick={() => moveOption(idx, -1)}
                disabled={idx === 0}
                title="Monter"
              >
                ↑
              </button>
              <button
                type="button"
                className={inline.choiceOptionMove}
                onClick={() => moveOption(idx, 1)}
                disabled={idx === item.options.length - 1}
                title="Descendre"
              >
                ↓
              </button>
              <button
                type="button"
                className={inline.choiceOptionAdd}
                onClick={() => addOptionAfter(idx)}
                title="Ajouter une réponse"
              >
                +
              </button>
              <button
                type="button"
                className={inline.choiceOptionRemove}
                onClick={() => removeOption(idx)}
                title="Supprimer cette réponse"
              >
                ×
              </button>
              </div>
            </div>
          </div>
          {(opt.pedaShields ?? 0) > 0 && (
            <div className={inline.choicePedaSection}>
              <span className={inline.metaFieldLabel}>Catégorie PEDA</span>
              <select
                className={inline.choicePedaSelect}
                value={opt.pedaCategory ?? ''}
                onChange={(e) =>
                  updateOption(idx, {
                    pedaCategory: (e.target.value || undefined) as PedaCategoryKey | undefined,
                  })
                }
                aria-label="Catégorie PEDA"
              >
                <option value="">— Sélectionner —</option>
                {PEDA_CATEGORY_KEYS.map((key) => (
                  <option key={key} value={key}>
                    {PEDA_CATEGORY_LABELS[key]}
                  </option>
                ))}
              </select>
            </div>
          )}
          {(opt.confidenceGains?.length ?? 0) > 0 && (
            <div className={inline.choiceConfidenceSection}>
              <div className={inline.choiceConfidenceRows}>
                {opt.confidenceGains!.map((gain, gainIdx) => (
                  <div key={gainIdx} className={inline.choiceConfidenceRow}>
                    <div className={inline.choiceConfidenceAmount}>
                      <span className={inline.metaFieldLabel}>+</span>
                      <input
                        type="number"
                        min={1}
                        max={100}
                        className={inline.choiceOptionQtyInput}
                        value={gain.amount}
                        onChange={(e) => {
                          const n = parseInt(e.target.value, 10);
                          const confidenceGains = [...(opt.confidenceGains ?? [])];
                          confidenceGains[gainIdx] = { ...gain, amount: n > 0 ? Math.min(100, n) : 1 };
                          updateOption(idx, { confidenceGains });
                        }}
                        aria-label="Points de confiance à ajouter"
                      />
                      <span className={inline.metaFieldLabel}>%</span>
                    </div>
                    <select
                      className={inline.choiceConfidenceSelect}
                      value={gain.characterId}
                      onChange={(e) => {
                        const confidenceGains = [...(opt.confidenceGains ?? [])];
                        confidenceGains[gainIdx] = { ...gain, characterId: e.target.value };
                        updateOption(idx, { confidenceGains });
                      }}
                    >
                      {characters.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                    {(opt.confidenceGains?.length ?? 0) > 1 && (
                      <button
                        type="button"
                        className={inline.choiceOptionRemove}
                        onClick={() => {
                          const confidenceGains = (opt.confidenceGains ?? []).filter((_, i) => i !== gainIdx);
                          updateOption(idx, {
                            confidenceGains: confidenceGains.length > 0 ? confidenceGains : undefined,
                          });
                        }}
                        title="Retirer ce personnage"
                      >
                        ×
                      </button>
                    )}
                    {gainIdx === (opt.confidenceGains?.length ?? 0) - 1 && (
                      <button
                        type="button"
                        className={`${studio.btnSecondary} ${inline.choiceConfidenceAddBtn}`}
                        onClick={() => {
                          const usedIds = new Set((opt.confidenceGains ?? []).map((g) => g.characterId));
                          const nextChar = characters.find((c) => !usedIds.has(c.id)) ?? characters[0];
                          if (!nextChar) return;
                          updateOption(idx, {
                            confidenceGains: [...(opt.confidenceGains ?? []), { characterId: nextChar.id, amount: 10 }],
                          });
                        }}
                        disabled={characters.length === 0}
                      >
                        + Personnage
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          {characters.length === 0 && (
            <span className={inline.choiceConfidenceHint}>
              Ajoutez des personnages dans l&apos;onglet Personnages pour activer la confiance.
            </span>
          )}
          {renderBranch(idx, opt.text)}
        </div>
      ))}
    </div>
  );
};

export function InlineChoiceReadonly({
  options,
  linkLabels,
  characters = [],
}: {
  options: Array<{
    text: string;
    pedaShields?: number;
    stopButton?: boolean;
    confidenceGains?: Array<{ characterId: string; amount: number }>;
  }>;
  linkLabels: string[];
  characters?: CharacterPortrait[];
}) {
  const charName = (characterId: string) =>
    characters.find((c) => c.id === characterId)?.name ?? characterId;

  return (
    <div className={inline.choiceRow}>
      {options.map((opt, i) => (
        <span key={i} className={inline.choiceBtn}>
          <span className={inline.choiceBtnLabel} aria-hidden>
            ▶
          </span>
          <span className={inline.choiceBtnLabel}>{opt.text || '…'}</span>
          {(opt.pedaShields ?? 0) > 0 && (
            <span className={inline.choiceShieldBadge}>🛡 {opt.pedaShields}</span>
          )}
          {(opt.confidenceGains ?? []).map((gain, gi) => (
            <span key={gi} className={inline.choiceConfidenceBadge}>
              💗 +{gain.amount} {charName(gain.characterId)}
            </span>
          ))}
          {opt.stopButton && (
            <span className={inline.choiceShieldBadge}>✋ STOP</span>
          )}
          {linkLabels[i] && <span className={inline.choiceDest}>→ {linkLabels[i]}</span>}
        </span>
      ))}
    </div>
  );
}
